import { PointTypeManagementService } from '../services/pointTypeManagementService';
import { ItemCardService } from '../services/itemCardService';
import { StudentItemCardService } from '../services/studentItemCardService';
import { StudentCoinService } from '../services/studentCoinService';
import { ClassMembersPartitionService } from '../services/classMembersPartitionService';


/**
 * 멤버의 레벨업 시 보상 아이템 및 코인 자동 지급
 * @param userId 사용자 ID
 * @param classId 클래스 ID
 * @param studentId 멤버 ID
 * @param oldLevel 이전 레벨
 * @param newLevel 새로운 레벨
 */
export async function grantLevelUpRewards(
  userId: string,
  classId: string,
  studentId: string,
  oldLevel: number,
  newLevel: number,
  memberGroup?: string
): Promise<void> {
  try {
    console.log(`🎁 레벨업 보상 지급 시작: 멤버 ${studentId}, ${oldLevel} -> ${newLevel}`);

    // 레벨업이 아니면 종료
    if (newLevel <= oldLevel) {
      return;
    }

    // 서비스 초기화
    const pointTypeService = new PointTypeManagementService(userId, classId);
    const itemCardService = new ItemCardService(userId, classId, memberGroup);
    const studentItemCardService = new StudentItemCardService(userId, classId);
    const studentCoinService = new StudentCoinService(userId, classId, memberGroup);

    // 멤버의 이미 보상받은 레벨 목록 조회 (그룹 프로필과 동일한 파티션에서 읽어야 함)
    const rewardedLevels = await getStudentRewardedLevels(userId, classId, studentId, memberGroup);

    // 레벨별 보상 데이터 로드
    const levelItemCards = await pointTypeService.getLevelItemCards();
    const levelCoins = await pointTypeService.getLevelCoins();

    const newlyRewardedLevels: number[] = [];
    const normalizeLevel = (value: any): number | null => {
      if (value === undefined || value === null || value === '') return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    // 이전에 잠금된 레벨업 아이템이 있으면 선제적으로 잠금 해제
    // - 레벨이 다시 올라갈 때, newLevel 이하의 레벨업 아이템은 항상 해제되어야 함
    try {
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      const member = await partitionService.getMember(studentId);
      const currentItemCards: any[] = Array.isArray((member as any)?.itemCards) ? [...(member as any).itemCards] : [];
      let preUnlockUpdated = false;

      const normalizeCategory = (category: any) => (category || '').toString().toLowerCase();
      for (const card of currentItemCards) {
        const acquiredLevel = normalizeLevel(card?.acquiredLevel);
        if (
          card?.isLocked === true &&
          normalizeCategory(card?.category) === 'levelup' &&
          acquiredLevel !== null &&
          acquiredLevel <= newLevel
        ) {
          card.isLocked = false;
          preUnlockUpdated = true;
          console.log(`    🔓 (선제) 아이템 잠금 해제: 레벨 ${acquiredLevel}, itemCardId=${card?.itemCardId}`);
        }
      }

      if (preUnlockUpdated) {
        await partitionService.updateStudentItemCardsAtomic(studentId, () => currentItemCards);
        try {
          const { userDataService } = await import('../services/userDataService');
          userDataService.invalidateMembersCache(userId, classId, memberGroup);
          console.log('🗑️ 레벨업 아이템 선제 잠금 해제 후 캐시 무효화 완료');
        } catch (error) {
          console.warn('레벨업 아이템 선제 잠금 해제 후 캐시 무효화 실패 (무시):', error);
        }
      }
    } catch (error) {
      console.warn('레벨업 아이템 선제 잠금 해제 실패 (무시):', error);
    }

    // 올라간 레벨들에 대해 보상 지급
    for (let level = oldLevel + 1; level <= newLevel; level++) {
      // 이미 보상을 받은 레벨인지 확인
      if (rewardedLevels.includes(level)) {
        console.log(`  ⏭️ 레벨 ${level} - 이미 보상 지급됨, 건너뜀`);
        continue;
      }

      let hasReward = false;

      // 1. 코인 지급
      const coinAmount = levelCoins[level];
      if (coinAmount && coinAmount > 0) {
        try {
          console.log(`  💰 레벨 ${level} 코인 지급 시작: ${coinAmount}코인, 타입: Levelup`);
          await studentCoinService.grantCoinsToStudent(
            studentId,
            coinAmount,
            `레벨 ${level} 달성 보상`,
            'Levelup'
          );
          console.log(`  ✅ 레벨 ${level} 도달 - ${coinAmount}코인 지급 완료 (타입: Levelup)`);
          hasReward = true;
        } catch (error) {
          console.error(`  ❌ 코인 지급 실패:`, error);
        }
      }

      // 2. 아이템 카드 지급 (잠금/해제 방식으로 통일)
      const itemCardIds = levelItemCards[level];
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      const member = await partitionService.getMember(studentId);
      const currentItemCards: any[] = Array.isArray((member as any)?.itemCards) ? [...(member as any).itemCards] : [];
      let itemCardsUpdated = false;

      const normalizeCategory = (category: any) => (category || '').toString().toLowerCase();

      // 2-1) 이전에 잠금된 레벨업 카드가 있으면 우선 잠금 해제 (레벨 설정 변경에도 대응)
      const lockedByLevel = currentItemCards.filter((c: any) =>
        c?.isLocked === true &&
        normalizeCategory(c?.category) === 'levelup' &&
        c?.acquiredLevel === level
      );
      if (lockedByLevel.length > 0) {
        for (const card of lockedByLevel) {
          card.isLocked = false;
          console.log(`    🔓 아이템 잠금 해제 완료 (레벨 ${level}, itemCardId=${card?.itemCardId})`);
        }
        itemCardsUpdated = true;
        hasReward = true;
      }

      if (itemCardIds && itemCardIds.length > 0) {
        console.log(`  📦 레벨 ${level} 도달 - ${itemCardIds.length}개의 아이템 지급`);

        for (const itemCardId of itemCardIds) {
          try {
            const itemCard = await itemCardService.getItemCard(itemCardId);
            
            if (itemCard) {
              const lockedItemIndex = currentItemCards.findIndex((c: any) =>
                c?.itemCardId === itemCardId &&
                normalizeCategory(c?.category) === 'levelup' &&
                normalizeLevel(c?.acquiredLevel) === level &&
                c?.isLocked === true
              );
              const existingItemIndex = currentItemCards.findIndex((c: any) =>
                c?.itemCardId === itemCardId &&
                normalizeCategory(c?.category) === 'levelup' &&
                normalizeLevel(c?.acquiredLevel) === level &&
                c?.isLocked !== true
              );

              if (lockedItemIndex !== -1) {
                currentItemCards[lockedItemIndex].isLocked = false;
                itemCardsUpdated = true;
                console.log(`    🔓 아이템 "${itemCard.name}" 잠금 해제 완료 (레벨 ${level})`);
                hasReward = true;
                continue;
              }

              if (existingItemIndex !== -1) {
                console.log(`    ⏭️ 아이템 "${itemCard.name}" 이미 지급됨 (레벨 ${level}), 건너뜀`);
                hasReward = true;
                continue;
              }

              // 잠금된 아이템도 없고 이미 받은 아이템도 없으면 새로 지급
              const { userDataService } = await import('../services/userDataService');
              const userProfile = await userDataService.loadUserProfile(userId);

              if (userProfile?.userType === 'free' || userProfile?.userType === 'basic') {
                console.log(`⚠️ 프리/베이직 플랜: 레벨업 아이템 카드 지급 제한 - ${itemCard.name} (레벨 ${level})`);
                console.log('📢 표시할 메시지: 스탠다드 이상의 플랜에서 아이템카드 사용이 가능합니다. 레벨업으로 지급되는 아이템 카드는 제한됩니다. 아이템 카드를 사용하기 위해서는 플랜을 업그레이드 해주세요.');
              } else {
                await studentItemCardService.grantItemCardToStudent(studentId, itemCard, { acquiredLevel: level });
                console.log(`    ✅ 아이템 "${itemCard.name}" 지급 완료 (레벨 ${level})`);
                hasReward = true;
              }
            } else {
              console.warn(`    ⚠️ 아이템 ${itemCardId}를 찾을 수 없습니다.`);
            }
          } catch (error) {
            console.error(`    ❌ 아이템 ${itemCardId} 지급 실패:`, error);
          }
        }
      }

      // 잠금 해제/생성된 아이템이 있으면 한 번에 업데이트
      if (itemCardsUpdated) {
        await partitionService.updateStudentItemCardsAtomic(studentId, () => currentItemCards);

        // 캐시 무효화 - 아이템 카드 상태 변경 즉시 반영
        try {
          const { userDataService } = await import('../services/userDataService');
          userDataService.invalidateMembersCache(userId, classId, memberGroup);
          console.log('🗑️ 레벨업 아이템 잠금 해제 후 캐시 무효화 완료');
        } catch (error) {
          console.warn('레벨업 아이템 잠금 해제 후 캐시 무효화 실패 (무시):', error);
        }
      }

      // 보상을 지급했으면 기록
      if (hasReward) {
        newlyRewardedLevels.push(level);
      }
    }

    // 보상 지급 레벨 기록
    if (newlyRewardedLevels.length > 0) {
      await markLevelsAsRewarded(userId, classId, studentId, newlyRewardedLevels, memberGroup);
    }

    console.log(`🎉 레벨업 보상 지급 완료: 멤버 ${studentId}`);
  } catch (error) {
    console.error('❌ 레벨업 보상 지급 중 오류 발생:', error);
  }
}

/**
 * 멤버의 레벨 다운(포인트 감소로 레벨이 낮아짐) 시 레벨업 보상 회수
 * - 코인: Levelup 타입으로 음수 지급(차감)하여 회수
 * - 아이템: category=levelup 아이템 잠금 처리하여 사용 중지
 * - rewardedLevels: 회수에 성공한 레벨만 목록에서 제거
 */
export async function revokeLevelDownRewards(
  userId: string,
  classId: string,
  studentId: string,
  oldLevel: number,
  newLevel: number,
  memberGroup?: string
): Promise<void> {
  try {
    console.log(`🧹 레벨다운 보상 회수 시작: 멤버 ${studentId}, ${oldLevel} -> ${newLevel}`);

    if (newLevel >= oldLevel) return;

    const pointTypeService = new PointTypeManagementService(userId, classId);
    const studentCoinService = new StudentCoinService(userId, classId, memberGroup);
    const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);

    const rewardedLevels = await getStudentRewardedLevels(userId, classId, studentId, memberGroup);
    const levelItemCards = await pointTypeService.getLevelItemCards();
    const levelCoins = await pointTypeService.getLevelCoins();

    const member = await partitionService.getMember(studentId);
    const currentItemCards: any[] = Array.isArray((member as any)?.itemCards) ? [...(member as any).itemCards] : [];

    const levelsToRevoke: number[] = [];
    let hasProcessedItems = false; // 아이템 처리를 했는지 추적

    for (let level = oldLevel; level > newLevel; level--) {
      if (!rewardedLevels.includes(level)) {
        continue;
      }

      let fullyReclaimed = true;

      // 1) 코인 회수
      const coinAmount = levelCoins[level];
      if (coinAmount && coinAmount > 0) {
        const currentCoins = await studentCoinService.getStudentCoins(studentId);
        const reclaimAmount = Math.min(currentCoins, coinAmount);

        if (reclaimAmount > 0) {
          await studentCoinService.grantCoinsToStudent(
            studentId,
            -reclaimAmount,
            `레벨 ${level} 달성 보상 회수`,
            'Levelup'
          );
          console.log(`  ✅ 레벨 ${level} 코인 회수: -${reclaimAmount}/${coinAmount}`);
        }

        // 코인이 부족하면 완전 회수 실패로 처리 (음수 잔액 방지)
        if (reclaimAmount < coinAmount) {
          console.warn(`  ⚠️ 레벨 ${level} 코인 회수 불완전: 현재 ${currentCoins}, 필요 ${coinAmount}`);
          fullyReclaimed = false;
        }
      }

      // 2) 아이템 잠금 처리 (보상 회수)
      const normalizeCategory = (category: any) => (category || '').toString().toLowerCase();
      const normalizeLevel = (value: any): number | null => {
        if (value === undefined || value === null || value === '') return null;
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
      };

      // acquiredLevel이 있는 레벨업 아이템만 정확히 잠금 (잘못된 레벨 잠금 방지)
      const levelMatchedItems = currentItemCards
        .map((c: any, i: number) => ({ c, i }))
        .filter(({ c }) =>
          normalizeCategory(c?.category) === 'levelup' &&
          normalizeLevel(c?.acquiredLevel) === level
        );

      if (levelMatchedItems.length > 0) {
        hasProcessedItems = true;
        for (const { c, i } of levelMatchedItems) {
          const wasLocked = currentItemCards[i].isLocked;
          currentItemCards[i].isLocked = true;
          console.log(`  🔒 레벨 ${level} 아이템 잠금 처리: itemCardId=${c?.itemCardId}, instanceId=${currentItemCards[i]?.instanceId}, 이전상태=${wasLocked}`);
        }
      } else {
        // acquiredLevel이 없는 구버전 아이템은 잘못된 레벨 잠금 위험이 있어 잠금하지 않음
        const itemCardIds = levelItemCards[level] || [];
        if (itemCardIds.length > 0) {
          console.warn(`  ⚠️ 레벨 ${level} 아이템 잠금 건너뜀: acquiredLevel 없는 아이템으로 추정`);
          fullyReclaimed = false;
        }
      }

      if (fullyReclaimed) {
        levelsToRevoke.push(level);
      }
    }

    // 아이템 목록 업데이트 (아이템 처리를 했거나 레벨 회수가 있었을 때)
    // 아이템이 잠금되었거나 코인이 회수되었으면 업데이트
    const hasItemChanges = currentItemCards.some((item: any) => item.isLocked === true);

    if (hasProcessedItems || hasItemChanges || levelsToRevoke.length > 0) {
      await partitionService.updateStudentItemCardsAtomic(studentId, () => currentItemCards);

      // 캐시 무효화 - 아이템 카드 상태 변경 즉시 반영
      try {
        const { userDataService } = await import('../services/userDataService');
        userDataService.invalidateMembersCache(userId, classId, memberGroup);
        console.log('🗑️ 레벨다운 아이템 잠금 후 캐시 무효화 완료');
      } catch (error) {
        console.warn('레벨다운 아이템 잠금 후 캐시 무효화 실패 (무시):', error);
      }

      // rewardedLevels는 레벨 다운·보상 회수 시에도 제거하지 않음 (한 번 기록된 보상 받은 레벨은 유지)
      // 재레벨업 시 이미 지급된 보상이 중복 지급되지 않도록 함.
    }

    console.log(`🧹 레벨다운 보상 회수 완료: 멤버 ${studentId}, 회수 레벨=${levelsToRevoke.join(',') || '(없음)'}`);
  } catch (error) {
    console.error('❌ 레벨다운 보상 회수 중 오류 발생:', error);
  }
}

/**
 * 멤버가 이미 보상받은 레벨 목록 조회
 */
async function getStudentRewardedLevels(
  userId: string,
  classId: string,
  studentId: string,
  memberGroup?: string
): Promise<number[]> {
  try {
    const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
    const member = await partitionService.getMember(studentId);
    return (member as any)?.rewardedLevels || [];
  } catch (error) {
    console.error('보상받은 레벨 조회 실패:', error);
    return [];
  }
}

/**
 * 멤버가 보상받은 레벨 기록
 */
async function markLevelsAsRewarded(
  userId: string,
  classId: string,
  studentId: string,
  levels: number[],
  memberGroup?: string
): Promise<void> {
  try {
    const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
    await partitionService.addStudentRewardedLevelsAtomic(studentId, levels);
  } catch (error) {
    console.error('보상받은 레벨 기록 실패:', error);
    throw error;
  }
}

/**
 * 레벨 계산
 * @param totalPoints 총 포인트
 * @param levelUpPoints 레벨업에 필요한 포인트
 * @returns 레벨
 */
export function calculateLevel(totalPoints: number, levelUpPoints: number): number {
  const unit = Math.max(1, levelUpPoints > 0 ? levelUpPoints : 50);
  return Math.floor(Math.max(0, totalPoints || 0) / unit);
}

/** 레벨 숫자 + 진행률(0–100) — PointsTablePage calculateLevel 과 동일 공식 */
export function getLevelDisplayInfo(
  totalPoints: number,
  levelUpPoints: number
): { level: number; progress: number } {
  const unit = Math.max(1, Number(levelUpPoints) > 0 ? Number(levelUpPoints) : 50);
  const safeTotal = Math.max(0, Number(totalPoints) || 0);
  const level = Math.floor(safeTotal / unit);
  const currentLevelStartPoints = level * unit;
  const currentLevelPoints = safeTotal - currentLevelStartPoints;
  const progress = unit > 0 ? (currentLevelPoints / unit) * 100 : 0;
  return { level, progress };
}

export function normalizeLevelUpPoints(value: unknown, fallback = 50): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * 포인트 기준 레벨은 올랐는데 rewardedLevels 기록이 뒤처진 경우(과제 동기화 등에서 currentLevel만 먼저 갱신된 경우 등)
 * 누락된 레벨 구간만 보상 지급 후 currentLevel을 맞춤.
 * @param rewardedLevelsHint 이미 조회한 멤버의 rewardedLevels가 있으면 전달해 중복 조회를 줄임. 미조회 시 undefined.
 */
export async function ensureLevelRewardsCatchUp(
  userId: string,
  classId: string,
  studentId: string,
  totalPoints: number,
  levelUpPoints: number,
  memberGroup?: string,
  rewardedLevelsHint?: number[]
): Promise<void> {
  const computedLevel = calculateLevel(totalPoints, levelUpPoints);
  const rewardedLevels =
    rewardedLevelsHint !== undefined
      ? [...rewardedLevelsHint]
      : await getStudentRewardedLevels(userId, classId, studentId, memberGroup);
  const maxRewardedLevel = rewardedLevels.length ? Math.max(...rewardedLevels) : 0;
  if (computedLevel <= maxRewardedLevel) return;

  await grantLevelUpRewards(
    userId,
    classId,
    studentId,
    maxRewardedLevel,
    computedLevel,
    memberGroup
  );
  const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
  await partitionService.updateStudentCurrentLevel(studentId, computedLevel);
  try {
    const { userDataService } = await import('../services/userDataService');
    userDataService.invalidateMembersCache(userId, classId, memberGroup);
  } catch {
    // ignore
  }
}

/**
 * 포인트 변경 시 레벨업 감지 및 보상 지급
 * @param userId 사용자 ID
 * @param classId 클래스 ID
 * @param studentId 멤버 ID
 * @param oldPoints 이전 포인트
 * @param newPoints 새로운 포인트
 * @param levelUpPoints 레벨업에 필요한 포인트
 */
export async function checkAndGrantLevelUpRewards(
  userId: string,
  classId: string,
  studentId: string,
  oldPoints: number,
  newPoints: number,
  levelUpPoints: number
): Promise<void> {
  const oldLevel = calculateLevel(oldPoints, levelUpPoints);
  const newLevel = calculateLevel(newPoints, levelUpPoints);

  if (newLevel > oldLevel) {
    await grantLevelUpRewards(userId, classId, studentId, oldLevel, newLevel);
  }
}

/**
 * 포인트 변화에 따른 레벨업/레벨다운 보상 동기화
 */
export async function syncLevelUpRewardsByPoints(
  userId: string,
  classId: string,
  studentId: string,
  oldPoints: number,
  newPoints: number,
  levelUpPoints: number,
  memberGroup?: string,
  providedOldLevel?: number,
  providedNewLevel?: number
): Promise<void> {
  // 제공된 레벨이 있으면 사용, 없으면 포인트로 계산
  const oldLevel = providedOldLevel !== undefined 
    ? providedOldLevel 
    : calculateLevel(oldPoints, levelUpPoints);
  const newLevel = providedNewLevel !== undefined 
    ? providedNewLevel 
    : calculateLevel(newPoints, levelUpPoints);

  console.log(`🔍 syncLevelUpRewardsByPoints 호출:`, {
    studentId,
    oldPoints,
    newPoints,
    levelUpPoints,
    oldLevel,
    newLevel,
    providedOldLevel,
    providedNewLevel,
    calculatedOldLevel: calculateLevel(oldPoints, levelUpPoints),
    calculatedNewLevel: calculateLevel(newPoints, levelUpPoints),
    levelChanged: newLevel !== oldLevel
  });

  // 레벨 변화가 있을 때만 처리
  if (newLevel !== oldLevel) {
    const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
    
    try {
      if (newLevel > oldLevel) {
        console.log(`📈 레벨업 처리 시작: ${oldLevel} → ${newLevel}`);
        await grantLevelUpRewards(userId, classId, studentId, oldLevel, newLevel, memberGroup);
      } else if (newLevel < oldLevel) {
        console.log(`📉 레벨다운 보상 회수 시작: ${oldLevel} → ${newLevel}`);
        await revokeLevelDownRewards(userId, classId, studentId, oldLevel, newLevel, memberGroup);
      }
    } catch (error) {
      console.error(`❌ 레벨 변화 보상 처리 실패 (${newLevel > oldLevel ? '레벨업' : '레벨다운'}):`, error);
      // 보상 처리 실패해도 currentLevel은 업데이트해야 함
    }
    
    // currentLevel 업데이트 (보상 처리 성공/실패와 관계없이 항상 업데이트)
    try {
      console.log(`🔄 currentLevel 업데이트 시도: ${studentId}, ${oldLevel} → ${newLevel}`);
      await partitionService.updateStudentCurrentLevel(studentId, newLevel);
      console.log(`✅ 멤버 ${studentId} currentLevel 업데이트 완료: ${oldLevel} → ${newLevel}`);

      // 업데이트 후 확인
      const member = await partitionService.getMember(studentId);
      const updatedCurrentLevel = (member as any)?.currentLevel;
      console.log(`🔍 업데이트 후 확인: currentLevel = ${updatedCurrentLevel}, 예상값 = ${newLevel}`);

      if (updatedCurrentLevel !== newLevel) {
        console.error(`❌ currentLevel 업데이트 불일치: 예상 ${newLevel}, 실제 ${updatedCurrentLevel}`);
      }

      // 레벨 변화 후 ClassMembers 캐시 무효화 (다음 조회 시 최신 데이터 반영)
      try {
        const { userDataService } = await import('../services/userDataService');
        userDataService.invalidateMembersCache(userId, classId, memberGroup);
        console.log('🗑️ 레벨 변화 후 ClassMembers 캐시 무효화 완료');
      } catch (error) {
        console.warn('레벨 변화 후 캐시 무효화 실패 (무시):', error);
      }
    } catch (error) {
      console.error(`❌ currentLevel 업데이트 실패:`, error);
      throw error; // currentLevel 업데이트 실패는 치명적이므로 에러를 다시 던짐
    }
  } else {
    console.log(`⏭️ 레벨 변화 없음, currentLevel 업데이트 건너뜀: ${oldLevel} = ${newLevel}`);
  }
}

